/**
 * Defaults from `import.meta.env` / `.env` (and dev URL query params).
 * `mount()` merges these with any credentials passed at mount time.
 *
 * Analytics uses the same public token as the Threekit player (`authToken`).
 * Analytics API host is `${env}.threekit.com` (same env subdomain as the player).
 */

export function readCpqEnvDefaults(): {
  env: string;
  authToken: string;
  assetId: string;
  orgId: string;
  /** Same-origin upload proxy path when set (e.g. `/api/threekit-files`). */
  filesUploadProxyPath: string;
  /** Same-origin (or absolute https) path to the AR QR endpoint (e.g. `/api/ar/qr`). */
  arQrEndpoint: string;
  /** Same-origin (or absolute https) path to the AR QR-upload endpoint (e.g. `/api/ar/qrUrl`). */
  arQrUrlEndpoint: string;
} {
  const params =
    import.meta.env.DEV && typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : null;

  return {
    env:
      params?.get("env") ??
      import.meta.env.VITE_THREEKIT_ENV ??
      "preview",
    authToken:
      params?.get("token") ??
      import.meta.env.VITE_THREEKIT_AUTH_TOKEN ??
      "",
    assetId:
      params?.get("asset") ??
      import.meta.env.VITE_THREEKIT_ASSET_ID ??
      "",
    orgId:
      params?.get("org") ??
      import.meta.env.VITE_THREEKIT_ORG_ID ??
      "",
    filesUploadProxyPath:
      (import.meta.env.VITE_THREEKIT_FILES_PROXY as string | undefined) ?? "",
    arQrEndpoint:
      (import.meta.env.VITE_THREEKIT_AR_QR_PROXY as string | undefined)?.trim() ||
      "/api/ar/qr",
    arQrUrlEndpoint:
      (import.meta.env.VITE_THREEKIT_AR_QR_URL_PROXY as string | undefined)?.trim() ||
      "/api/ar/qrUrl",
  };
}

/** Analytics host for `@threekit/analytics` from the Threekit env subdomain. */
export function analyticsHostForThreekitEnv(env: string): string {
  const e = env.trim() || "preview";
  return e.endsWith(".threekit.com") ? e : `${e}.threekit.com`;
}

export const EMBED_CREDENTIALS_ERROR =
  "Threekit CPQ embed: provide authToken, assetId, and orgId on mount(), or set VITE_THREEKIT_AUTH_TOKEN, VITE_THREEKIT_ASSET_ID, and VITE_THREEKIT_ORG_ID in .env (see .env.example). In dev you can use query params token, asset, and org instead.";
